import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthShell } from '@/components/auth/AuthShell';
import { OtpInput } from '@/components/auth/OtpInput';
import { NeonButton } from '@/components/ui/NeonButton';
import { Icon } from '@/components/ui/Icon';
import { useAuth } from '@/modules/auth/useAuth';
import { ROUTES } from '@/config/routes';

/**
 * MFA challenge — invoked after a successful password login when the
 * account has a TOTP factor enrolled. The factor id is passed via
 * `location.state.factorId`. On verify, navigate to wherever the user
 * was headed (or /feed by default).
 */
interface MfaState {
  factorId?: string;
  next?: string;
}

export const MfaChallengePage = () => {
  const { challengeAndVerify, listFactors } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as MfaState | null) ?? {};

  const [factorId, setFactorId] = useState<string | undefined>(state.factorId);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (factorId) return;
    // Fallback: caller didn't pass a factorId — pick the first verified TOTP.
    (async () => {
      const factors = await listFactors();
      const verified = factors.find((f) => f.status === 'verified');
      if (verified) setFactorId(verified.id);
    })();
  }, [factorId, listFactors]);

  const onVerify = async (entered: string) => {
    if (!factorId || entered.length !== 6 || submitting) return;
    setError(null);
    setSubmitting(true);
    const result = await challengeAndVerify(factorId, entered);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'Wrong code. Try again.');
      setCode('');
      return;
    }
    navigate(state.next ?? ROUTES.feed, { replace: true });
  };

  return (
    <AuthShell
      eyebrow="Second factor"
      title={
        <>
          Open your authenticator<span className="text-pink">.</span>
        </>
      }
      intro={
        <>
          Six digits from your authenticator app — Google Authenticator, 1Password,
          Authy<span className="text-pink">.</span>
        </>
      }
      footer={
        <p className="text-center font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
          Lost your authenticator?{' '}
          <Link to={ROUTES.forgot} className="text-pink hover:underline">
            Reset access
          </Link>
        </p>
      }
    >
      <div className="flex flex-col gap-5">
        <OtpInput
          value={code}
          onChange={setCode}
          onComplete={onVerify}
          disabled={submitting}
        />
        {error && (
          <p className="font-display text-[10px] font-bold uppercase tracking-widest text-pink">
            — {error}
          </p>
        )}
        <NeonButton
          type="button"
          size="md"
          disabled={code.length !== 6 || submitting || !factorId}
          onClick={() => onVerify(code)}
          trailing={<Icon name="arrow_forward" size={14} />}
        >
          {submitting ? 'Verifying' : 'Continue'}
        </NeonButton>
      </div>
    </AuthShell>
  );
};
